///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GtthHistoryService } from '../../logical-topology/engine/history-service';
import { GtthUpdateMainConfigCommand } from '../../logical-topology/engine/commands/project/update-main-config-command';
import { ProjectWindowsPopupService } from '../project-windows-popup.service';
import {
    GtthBomSummaryDTO,
    GtthLayeredBomSummaryDTO,
    ProductAccessoryTypeEnum,
    ProductRadioDTO,
    ProductServiceDTO,
    ProductServiceTypeEnum
} from '../../../generated/shared-types.model';
import { ProductService } from '../../../services/product/product.service';
import { GtthExportService } from '../../../services/project/export.service';
import { GtthProjectStateService } from '../../logical-topology/engine/project-state.service';
import { ActiveRestorableWindow } from '../active-restorable-window';
import { MatDialogRef } from '@angular/material/dialog';
import { BomSummaryService } from './bom-summary.service';
import { BomSummaryDTO, BomSummaryRecordDTO, LayeredBomSummaryDTO } from './bom-summary.models';
import { finalize, forkJoin } from 'rxjs';
import { GtthBomReportService } from '../bom_report/bom-report.service';
import { SuiTypeEnum } from '@siklu/sui-lib/infrastructure';
import { _round } from '../../../shared/utils';
import { GtthCustomerLocationService } from '../../../services/customer/customer-location.service';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { ProjectProductsService } from '../../logical-topology/engine/project-products-service';
import { UserAuthorities } from '../../../services/user/user-authorities';

export enum ServiceYearsEnum {
    ONE_YEAR = 1,
    THREE_YEARS = 3,
    FIVE_YEARS = 5
}

export enum BOMSummaryTab {
    NEW = 'NEW',
    INSTALLED = 'INSTALLED'
}

@Component({
    selector: 'winde-project-gtth-bom-summary',
    templateUrl: './bom-summary.component.html',
    styleUrls: ['./bom-summary.component.scss'],
    standalone: false
})
export class GtthBomSummaryWindowComponent extends ActiveRestorableWindow implements OnInit {
    public bomSummaryDTO: LayeredBomSummaryDTO;
    public bomSummaryLoaderKey: LoaderIds = LoaderIds.BOM_SUMMARY_PRELOADER_ID;
    public currentTab = 0;
    public tabsToShow: string[] = [BOMSummaryTab.NEW, BOMSummaryTab.INSTALLED];
    public productServiceTypeEnum = ProductServiceTypeEnum;
    public productAccessoryEnum = ProductAccessoryTypeEnum;
    protected readonly userAuthorities = UserAuthorities;
    private services: ProductServiceDTO[];

    constructor(
        public readonly dialogRef: MatDialogRef<GtthBomSummaryWindowComponent>,
        public readonly historyService: GtthHistoryService,
        public readonly preloaderService: PreloaderService,
        private readonly bomReportService: GtthBomReportService,
        private readonly productService: ProductService,
        private readonly exportService: GtthExportService,
        private readonly projectStateService: GtthProjectStateService,
        private readonly bomSummaryService: BomSummaryService,
        private readonly customerLocationService: GtthCustomerLocationService
    ) {
        super();
    }

    ngOnInit() {
        this.preloaderService.show(this.bomSummaryLoaderKey);

        forkJoin([
            this.productService.queryForProject(this.projectStateService.getProjectId()),
            this.bomSummaryService.getBomSummary(this.projectStateService.getProjectId(), this.bomReportService.getBomForExport())
        ])
            .pipe(finalize(() => this.preloaderService.hide(this.bomSummaryLoaderKey)))
            .subscribe(([services, report]) => {
                this.services = services;
                this.bomSummaryDTO = report;
                this.initServiceControls();

                this.bomSummaryDTO.summaryPerLayer.forEach((layerSummaryDto) => {
                    this.initServicesForProducts(layerSummaryDto);
                });

                this.calculateTotalCosts();
            });
    }

    clear() {
        if (this.historyService.isEditModeEnabled) {
            this.historyService.doCommand(this.historyService.command(GtthUpdateMainConfigCommand));
        }
        this.dialogRef.close('cancel');
    }

    exportBomSummary() {
        this.bomSummaryDTO.currencySymbol = this.customerLocationService.getCurrencySymbolByCode(this.bomSummaryDTO.currencyCode);
        this.exportService.exportBomSummaryToExcel(
            this.convertSummaryToDTO(),
            this.historyService.logicalTopology.activeLayerIdx,
            this.bomSummaryLoaderKey
        );
    }

    exportBomSummaryInSalesForceFormat() {
        this.bomSummaryDTO.currencySymbol = this.customerLocationService.getCurrencySymbolByCode(this.bomSummaryDTO.currencyCode);
        this.exportService.exportBomSummaryInSalesForceFormat(this.convertSummaryToDTO(), this.bomSummaryLoaderKey);
    }

    changeTab(tabName: string) {
        this.currentTab = this.tabsToShow.findIndex((tab) => tab === tabName);
    }

    designRequired(): boolean {
        return true;
    }

    isRestorable(): boolean {
        return true;
    }

    public discountChanged(summaryRecord: BomSummaryRecordDTO): void {
        this.historyService.logicalTopology.products_discounts[summaryRecord.name] = summaryRecord.discount;
        this.recalculateSummaryRecordCost(summaryRecord);
        this.calculateTotalCosts();
    }

    private convertSummaryToDTO(): GtthLayeredBomSummaryDTO {
        const dto = {} as GtthLayeredBomSummaryDTO;
        dto.currencyCode = this.bomSummaryDTO.currencyCode;
        dto.currencySymbol = this.bomSummaryDTO.currencySymbol;

        dto.summaryPerLayer = this.bomSummaryDTO?.summaryPerLayer?.map((summaryForLayer) => {
            return {
                services: summaryForLayer.services,
                typeName: summaryForLayer.typeName,
                accessoriesMap: summaryForLayer.accessoriesMap,
                layerName: summaryForLayer.layerName
            } as GtthBomSummaryDTO;
        });

        return dto;
    }

    private initServiceControls(): void {
        this.bomSummaryDTO.summaryPerLayer.forEach((layerSummaryDto, layerIndex) => {
            if (!layerSummaryDto.typesCount) {
                return;
            }

            layerSummaryDto.serviceTypesSelect = new SuiTypeEnum(
                this.historyService.logicalTopology.selectedServiceTypePerLayer[layerIndex],
                [ProductServiceTypeEnum.NONE, ProductServiceTypeEnum.PLAN_PRO, ProductServiceTypeEnum.PLAN_ELITE],
                'ProductServiceTypeEnum'
            );
            layerSummaryDto.serviceYearsSelect = new SuiTypeEnum(
                this.historyService.logicalTopology.selectedServiceYearsPerLayer[layerIndex],
                [ServiceYearsEnum.ONE_YEAR, ServiceYearsEnum.THREE_YEARS, ServiceYearsEnum.FIVE_YEARS],
                'ServiceYearsEnum'
            );

            layerSummaryDto.serviceTypesSelect.valueChangedCallback = (newServiceType: ProductServiceTypeEnum) => {
                this.historyService.logicalTopology.selectedServiceTypePerLayer[layerIndex] = newServiceType;
                this.initServicesForProducts(layerSummaryDto);
                this.calculateTotalCosts();
            };
            layerSummaryDto.serviceYearsSelect.valueChangedCallback = (newServiceYear: number) => {
                this.historyService.logicalTopology.selectedServiceYearsPerLayer[layerIndex] = newServiceYear;
                this.initServicesForProducts(layerSummaryDto);
                this.calculateTotalCosts();
            };
        });
    }

    private initServicesForProducts(layerSummaryDto: BomSummaryDTO): void {
        layerSummaryDto.services = {};
        layerSummaryDto.servicesCount = 0;
        layerSummaryDto.installedServicesCount = 0;

        if (!layerSummaryDto.serviceTypesSelect || layerSummaryDto.serviceTypesSelect.value === ProductServiceTypeEnum.NONE) {
            return;
        }

        for (const productName in layerSummaryDto.typeName) {
            const productSummaryRecord = layerSummaryDto.typeName[productName];

            const relativeService = this.getProductService(
                productName,
                layerSummaryDto.serviceTypesSelect.value,
                layerSummaryDto.serviceYearsSelect.value
            );

            if (!relativeService) {
                continue;
            }
            if (!layerSummaryDto.services[relativeService.name]) {
                const discount = Math.max(
                    this.historyService.logicalTopology.basic_discount,
                    this.historyService.logicalTopology.products_discounts[relativeService.name] || 0
                );
                const summaryRecord = {
                    name: relativeService.name,
                    description: relativeService.description,
                    order: productSummaryRecord.order,
                    listPrice: relativeService.price,
                    discount: discount,
                    count: 0,
                    installedCount: 0,
                    cost: 0,
                    installedCost: 0
                } as BomSummaryRecordDTO;

                layerSummaryDto.services[relativeService.name] = summaryRecord;
            }

            if (productSummaryRecord.name.startsWith(ProjectProductsService.MPL_PRODUCT_PREFIX)) {
                layerSummaryDto.services[relativeService.name].count += productSummaryRecord.count * 2;
                layerSummaryDto.services[relativeService.name].installedCount += productSummaryRecord.installedCount * 2;
            } else {
                layerSummaryDto.services[relativeService.name].count += productSummaryRecord.count;
                layerSummaryDto.services[relativeService.name].installedCount += productSummaryRecord.installedCount;
            }
        }

        for (const servicesKey in layerSummaryDto.services) {
            layerSummaryDto.servicesCount += layerSummaryDto.services[servicesKey].count;
            layerSummaryDto.installedServicesCount += layerSummaryDto.services[servicesKey].installedCount;
        }
    }

    private recalculateSummaryRecordCost(summaryRecord: BomSummaryRecordDTO): void {
        summaryRecord.cost = _round((summaryRecord.count * summaryRecord.listPrice * (100 - summaryRecord.discount)) / 100, 2);
        summaryRecord.installedCost = _round(
            (summaryRecord.installedCount * summaryRecord.listPrice * (100 - summaryRecord.discount)) / 100,
            2
        );
    }

    private calculateTotalCosts(): void {
        this.bomSummaryDTO.totalCost = 0;
        this.bomSummaryDTO.totalInstalledCost = 0;

        this.bomSummaryDTO.summaryPerLayer.forEach((summaryPerLayer) => {
            this.calculateTotalCostPerLayer(summaryPerLayer);
            this.bomSummaryDTO.totalCost += summaryPerLayer.totalCostForLayer;
            this.bomSummaryDTO.totalInstalledCost += summaryPerLayer.totalInstalledCostForLayer;
        });

        this.bomSummaryDTO.totalCost = _round(this.bomSummaryDTO.totalCost, 2);
        this.bomSummaryDTO.totalInstalledCost = _round(this.bomSummaryDTO.totalInstalledCost, 2);
    }

    private calculateTotalCostPerLayer(summaryPerLayer: BomSummaryDTO): void {
        summaryPerLayer.totalCostForLayer = 0;
        summaryPerLayer.totalInstalledCostForLayer = 0;

        for (const productName in summaryPerLayer.typeName) {
            const summaryRecord = summaryPerLayer.typeName[productName];
            this.recalculateSummaryRecordCost(summaryRecord);
            summaryPerLayer.totalCostForLayer += summaryRecord.cost;
            summaryPerLayer.totalInstalledCostForLayer += summaryRecord.installedCost;
        }

        for (const serviceName in summaryPerLayer.services) {
            const summaryRecord = summaryPerLayer.services[serviceName];
            this.recalculateSummaryRecordCost(summaryRecord);
            summaryPerLayer.totalCostForLayer += summaryRecord.cost;
            summaryPerLayer.totalInstalledCostForLayer += summaryRecord.installedCost;
        }

        for (const accessoryType in summaryPerLayer.accessoriesMap) {
            const accessoriesByType = summaryPerLayer.accessoriesMap[accessoryType];
            for (const accessory in accessoriesByType) {
                const summaryRecord = accessoriesByType[accessory];
                this.recalculateSummaryRecordCost(summaryRecord);
                summaryPerLayer.totalCostForLayer += summaryRecord.cost;
                summaryPerLayer.totalInstalledCostForLayer += summaryRecord.installedCost;
            }
        }

        summaryPerLayer.totalCostForLayer = _round(summaryPerLayer.totalCostForLayer, 2);
        summaryPerLayer.totalInstalledCostForLayer = _round(summaryPerLayer.totalInstalledCostForLayer, 2);
    }

    private getProductService(typeName: string, serviceType: ProductServiceTypeEnum, numberOfYears: number): ProductServiceDTO {
        const matchingServices = this.services
            .filter((service: ProductServiceDTO) => service.serviceType === serviceType && service.yearsNumber === numberOfYears)
            .filter((service: ProductServiceDTO) => service.productRadios.some((radio: ProductRadioDTO) => radio.name === typeName));

        return matchingServices[0];
    }
}

@Component({
    selector: 'winde-project-gtth-bom-summary-popup',
    template: '',
    standalone: false
})
export class ProjectGtthBomSummaryPopupComponent implements OnInit {
    constructor(
        private readonly router: Router,
        private readonly historyService: GtthHistoryService,
        private readonly projectWindowsPopupService: ProjectWindowsPopupService
    ) {}

    ngOnInit() {
        if (!this.historyService.logicalTopology) {
            this.router.navigate([{ outlets: { popup: null } }], { replaceUrl: true });
            return;
        }
        this.projectWindowsPopupService.openWindow(GtthBomSummaryWindowComponent, 'xl');
    }
}
